import { useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconFilter, IconLayoutGrid, IconListDetails } from "@tabler/icons-react";
import type { ProductDto, ProductsQueryDto } from "../../../../api";
import { useProducts } from "../../hooks/useProducts";
import { ProductsGridView } from "../components/products-grid-view";
import { ProductsListView } from "../components/products-list-view";
import { ProductsFilterContent } from "../components/products-filter-content";
import { ProductsCompareBanner } from "../components/products-compare-banner";
import { CompareFlyLayer } from "../components/compare-fly-layer";
import { SearchBar } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { Heading } from "../../../../components/common/typography";
import { ViewToggle } from "../../../../components/common/view-toggle";
import { Drawer } from "../../../../components/common/drawer";
import { useWishlistStore } from "../../domain/stores/useWishlistStore";
import { useSearchFiltersUrlSync } from "../../../pagination/hooks/useSearchFiltersUrlSync";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { ProductCardProps } from "../components/product-card";
import { computeDiscount, getStockLabel, getWishlistVariantId } from "../../utils/card-props";
import { useActiveLoyaltyPromotions } from "../../../loyalty/hooks/useLoyaltyPromotions";
import { getActivePromotionForProduct } from "../../../loyalty/utils/resolve-effective-multiplier";
import { FeaturedDealsRow } from "../../../featured-deals/presentation/components/featured-deals-row";
import { useFeaturedDealProducts } from "../../../featured-deals/hooks/useFeaturedDealProducts";
import { IconX } from "@tabler/icons-react";

type ViewMode = "grid" | "list";

const VIEW_MODE_KEY = "catalog-view-mode";
const DEFAULT_maxCompare = 3;

interface ProductsPageProps {
  /** Maximum number of products that can be compared at once. */
  maxCompare?: number;
}

const viewOptions = [
  { value: "list" as const, icon: <IconListDetails size={20} />, "aria-label": "List view" },
  { value: "grid" as const, icon: <IconLayoutGrid size={20} />, "aria-label": "Grid view" },
] as const;

export function ProductsPage({ maxCompare = DEFAULT_maxCompare }: ProductsPageProps = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dealOfWeekOnly = searchParams.get("dealOfWeek") === "1";
  const clearDealOfWeek = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("dealOfWeek");
      next.set("page", "1");
      return next;
    }, { replace: true });
  };
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) ?? "grid",
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductDto[]>([]);

  const { searchValue, setSearchValue, filters, setFilters, baseQuery } =
    useSearchFiltersUrlSync<ProductsQueryDto>({
      initialFilters: {} as Record<string, unknown>,
      enableSearch: true,
    });

  // Always call both hooks regardless of mode — React's rules-of-hooks forbid
  // swapping hook identities between renders. The inactive one resolves from
  // SWR cache and costs nothing.
  const allProducts = useProducts(dealOfWeekOnly ? undefined : (baseQuery as ProductsQueryDto));
  const featuredProducts = useFeaturedDealProducts(
    dealOfWeekOnly ? (baseQuery as ProductsQueryDto) : undefined,
  );
  const productsData = dealOfWeekOnly ? featuredProducts.data : allProducts.data;
  const totalCount = productsData?.meta?.totalCount;

  const wishlistIds = useWishlistStore((s) => s.wishlistVariantIds);
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const { data: activePromotions } = useActiveLoyaltyPromotions();

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const activeFilterCount = Object.values(filters as Record<string, unknown>).filter(
    (v) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0),
  ).length;

  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map((p) => p.id)),
    [selectedProducts],
  );

  const handleProductToggle = useCallback((product: ProductDto) => {
    setSelectedProducts((prev) => {
      const exists = prev.some((p) => p.id === product.id);
      if (exists) return prev.filter((p) => p.id !== product.id);
      if (prev.length >= maxCompare) return prev;
      return [...prev, product];
    });
  }, [maxCompare]);

  const handleAddToCompare = useCallback((product: ProductDto) => {
    setSelectedProducts((prev) =>
      prev.length >= maxCompare || prev.some((p) => p.id === product.id)
        ? prev
        : [...prev, product],
    );
  }, [maxCompare]);

  const compareDisabled = selectedProducts.length >= maxCompare;

  const handleCompareNow = () => {
    const ids = selectedProducts.map((p) => p.id).join(",");
    navigate(`${AppRoutes.PRODUCTS_COMPARE}?ids=${ids}`, {
      state: {
        breadcrumbTrail: [{ label: "Catalog", path: AppRoutes.PRODUCTS }],
      },
    });
  };

  const handleClearCompare = () => {
    setSelectedProducts([]);
    setCompareMode(false);
  };

  const cardPropsGetter = useCallback((product: ProductDto): Partial<ProductCardProps> => {
    const discount = computeDiscount(product);
    const variantId = getWishlistVariantId(product);
    const promo = getActivePromotionForProduct(product.id, activePromotions);
    const mult = promo?.multiplier ?? 1;
    return {
      discountLabel: discount ? `${discount}% off` : undefined,
      loyaltyLabel: promo ? `${mult % 1 === 0 ? mult.toFixed(0) : mult.toFixed(1)}× points` : undefined,
      originalPrice: discount ? (product.standardPrice ?? undefined) : undefined,
      stockLabel: getStockLabel(product),
      isInWishlist: variantId != null && wishlistIds.includes(variantId),
      onToggleWishlist:
        variantId != null ? () => void toggleWishlist(variantId) : undefined,
    };
  }, [wishlistIds, toggleWishlist, activePromotions]);

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6 pb-24 max-md:px-4">
      <div className="flex items-center justify-between gap-4 max-md:flex-wrap max-md:gap-3">
        <div className="min-w-[280px] flex-1 lg:max-w-[700px] max-md:min-w-0 max-md:basis-full">
          <SearchBar
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search"
          />
        </div>
        <div className="flex shrink-0 items-center gap-3 max-md:w-full">
          <ViewToggle
            options={viewOptions}
            value={viewMode}
            onChange={handleViewModeChange}
            aria-label="Catalog view mode"
          />
          <Button
            variant={compareMode ? "primary" : "secondary"}
            size="md"
            onClick={() => {
              setCompareMode((v) => !v);
              if (compareMode) setSelectedProducts([]);
            }}
          >
            Compare
          </Button>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<IconFilter size={16} />}
            onClick={() => setFilterOpen(true)}
          >
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
        </div>
      </div>

      {!dealOfWeekOnly && <FeaturedDealsRow limit={4} showViewMore />}

      <div className="flex items-center justify-between gap-4">
        <Heading variant="h2" tone="dark">
          {dealOfWeekOnly
            ? `Deal of the Week${typeof totalCount === "number" ? ` (${totalCount})` : ""}`
            : typeof totalCount === "number"
              ? `(${totalCount.toLocaleString()}+ products available)`
              : "All Products"}
        </Heading>
        {dealOfWeekOnly && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<IconX size={14} />}
            onClick={clearDealOfWeek}
          >
            Show all products
          </Button>
        )}
      </div>

      {/* `key` forces a remount when the active hook changes — the catalog
          grid/list call useDataHook(query) internally; if the hook identity
          flipped without remounting, the internal hook order would shift. */}
      {viewMode === "grid" ? (
        <ProductsGridView<ProductsQueryDto>
          key={dealOfWeekOnly ? "deals" : "all"}
          useDataHook={dealOfWeekOnly ? useFeaturedDealProducts : useProducts}
          baseQuery={baseQuery as ProductsQueryDto}
          pageSize={12}
          cardPropsGetter={cardPropsGetter}
          compareMode={compareMode}
          selectedProducts={selectedProductIds}
          onProductToggle={handleProductToggle}
          compareDisabled={compareDisabled}
        />
      ) : (
        <ProductsListView<ProductsQueryDto>
          key={dealOfWeekOnly ? "deals" : "all"}
          useDataHook={dealOfWeekOnly ? useFeaturedDealProducts : useProducts}
          baseQuery={baseQuery as ProductsQueryDto}
          pageSize={10}
          cardPropsGetter={cardPropsGetter}
          compareMode={compareMode}
          selectedProducts={selectedProductIds}
          onProductToggle={handleProductToggle}
          compareDisabled={compareDisabled}
        />
      )}

      <Drawer
        open={filterOpen}
        close={() => setFilterOpen(false)}
        title="Filter Products"
        content={ProductsFilterContent({
          filters: filters as ProductsQueryDto,
          onFiltersChange: (f) => { setFilters(f); setFilterOpen(false); },
        })}
      />

      <ProductsCompareBanner
        selectedProducts={selectedProducts}
        maxCompare={maxCompare}
        onRemove={(id) => setSelectedProducts((prev) => prev.filter((p) => p.id !== id))}
        onAddProduct={handleAddToCompare}
        onCompare={handleCompareNow}
        onClear={handleClearCompare}
      />

      <CompareFlyLayer />
    </div>
  );
}

export default ProductsPage;
